import camelcaseKeys from 'camelcase-keys';
import { createStore } from 'dashboard/store/storeFactory';
import { throwErrorMessage } from 'dashboard/store/utils/api';
import TasksAPI from 'dashboard/api/tasksApi';

const camelizeTask = data => camelcaseKeys(data || {}, { deep: true });

export const useTasksStore = createStore({
  name: 'tasks',
  type: 'pinia',
  API: TasksAPI,
  state: () => ({}),

  getters: {
    getTasksList: state => state.records,
    getTasksByConversation: state => conversationId =>
      state.records.filter(t => t.conversationId === conversationId),
    getPendingTasksByConversation: state => conversationId =>
      state.records.filter(
        t => t.conversationId === conversationId && t.status === 'pending'
      ),
  },

  actions: () => ({
    upsertTask(record) {
      const index = this.records.findIndex(r => r.id === record.id);
      if (index === -1) this.records.push(record);
      else this.records[index] = record;
    },

    async get(filters = {}) {
      this.setUIFlag({ fetchingList: true });
      try {
        const {
          data: { payload },
        } = await TasksAPI.get(filters);
        this.records = camelizeTask(payload);
        return this.records;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async show(id) {
      const {
        data: { payload },
      } = await TasksAPI.show(id);
      return camelizeTask(payload);
    },

    async fetchStats(assignedToId) {
      const { data } = await TasksAPI.stats({ assigned_to_id: assignedToId });
      return camelizeTask(data);
    },

    async getByConversation(conversationId) {
      const {
        data: { payload },
      } = await TasksAPI.get({ conversation_id: conversationId });
      return camelizeTask(payload);
    },

    async create(taskAttrs) {
      this.setUIFlag({ creatingItem: true });
      try {
        const {
          data: { payload },
        } = await TasksAPI.create(taskAttrs);
        const task = camelizeTask(payload);
        this.upsertTask(task);
        return task;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ creatingItem: false });
      }
    },

    async update({ id, scope, occurrenceDate, ...taskAttrs }) {
      this.setUIFlag({ updatingItem: true });
      try {
        const {
          data: { payload },
        } = await TasksAPI.update(id, taskAttrs, { scope, occurrenceDate });
        const task = camelizeTask(payload);
        this.upsertTask(task);
        return task;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ updatingItem: false });
      }
    },

    async delete(id, options = {}) {
      this.setUIFlag({ deletingItem: true });
      try {
        await TasksAPI.destroy(id, options);
        this.records = this.records.filter(r => r.id !== Number(id));
        return Number(id);
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ deletingItem: false });
      }
    },
  }),
});
